import React from "react";
import {Box, Divider, Stack} from "@mui/material";
import FilterCenterFocusOutlinedIcon from "@mui/icons-material/FilterCenterFocusOutlined";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import RedoIcon from "@mui/icons-material/Redo";
import UndoIcon from "@mui/icons-material/Undo";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import {useTranslation} from "react-i18next";
import {useEditorState} from "@tiptap/react";
import type {Editor} from "@tiptap/core";
import type {PrintoutType} from "crqlar-react-common/dist/models/printout/printout.model";
import ToolbarBlockMenus from "./ToolbarBlockMenus";
import ToolbarColorMenus from "./ToolbarColorMenus";
import ToolbarMoreMenu from "./ToolbarMoreMenu";
import ToolbarVariableMenu from "./ToolbarVariableMenu";
import {ToolbarIconButton} from "./toolbarControls";
import {
    DEFAULT_TOOLBAR_STATE,
    getToolbarState,
    toggleSelectedPrintoutVariableMark,
} from "./printoutToolbarState";

export interface ToolbarZoomControls {
    canZoomIn: boolean;
    canZoomOut: boolean;
    onFitZoom: () => void;
    onZoomIn: () => void;
    onZoomOut: () => void;
}

const clearFormatting = (editor: Editor) => {
    editor.chain().focus().unsetAllMarks().clearNodes().run();
};

interface Props {
    active: boolean;
    editor: Editor | null;
    printoutType: PrintoutType | null;
    zoomControls: ToolbarZoomControls;
}

const PrintoutEditorToolbar = ({active, editor, printoutType, zoomControls}: Props) => {

    const {t} = useTranslation();
    const commandEditor = active ? editor : null;

    const currentToolbarState = useEditorState({
        editor,
        selector: ({editor}) => editor ? getToolbarState(editor) : DEFAULT_TOOLBAR_STATE,
    }) ?? DEFAULT_TOOLBAR_STATE;
    const toolbarState = active ? currentToolbarState : DEFAULT_TOOLBAR_STATE;

    const runCommand = (command: (editor: Editor) => void) => {
        if (!commandEditor || commandEditor.isDestroyed) return;
        command(commandEditor);
    };

    return (
        <Box
            sx={{
                bgcolor: 'background.paper',
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                px: 1.5,
                py: 1,
            }}
        >
            <Stack
                alignItems="center"
                direction="row"
                flexWrap="wrap"
                gap={0.5}
                sx={{width: '100%'}}
            >
                <ToolbarIconButton
                    disabled={!zoomControls.canZoomOut}
                    label={t('Zoom out')}
                    onClick={zoomControls.onZoomOut}
                >
                    <ZoomOutIcon fontSize="small"/>
                </ToolbarIconButton>
                <ToolbarIconButton
                    disabled={!zoomControls.canZoomIn}
                    label={t('Zoom in')}
                    onClick={zoomControls.onZoomIn}
                >
                    <ZoomInIcon fontSize="small"/>
                </ToolbarIconButton>
                <ToolbarIconButton
                    label={t('Fit to width')}
                    onClick={zoomControls.onFitZoom}
                >
                    <FilterCenterFocusOutlinedIcon fontSize="small"/>
                </ToolbarIconButton>

                <Divider flexItem orientation="vertical"/>

                <ToolbarBlockMenus editor={commandEditor} state={toolbarState}/>

                <Divider flexItem orientation="vertical"/>

                <ToolbarIconButton
                    active={toolbarState.isBold}
                    disabled={!toolbarState.canBold}
                    label={t('Bold')}
                    onClick={() => runCommand((editor) => {
                        if (toggleSelectedPrintoutVariableMark(editor, 'bold')) return;
                        editor.chain().focus().toggleBold().run();
                    })}
                >
                    <FormatBoldIcon fontSize="small"/>
                </ToolbarIconButton>
                <ToolbarIconButton
                    active={toolbarState.isItalic}
                    disabled={!toolbarState.canItalic}
                    label={t('Italic')}
                    onClick={() => runCommand((editor) => {
                        if (toggleSelectedPrintoutVariableMark(editor, 'italic')) return;
                        editor.chain().focus().toggleItalic().run();
                    })}
                >
                    <FormatItalicIcon fontSize="small"/>
                </ToolbarIconButton>
                <ToolbarIconButton
                    active={toolbarState.isUnderline}
                    disabled={!toolbarState.canUnderline}
                    label={t('Underline')}
                    onClick={() => runCommand((editor) => {
                        if (toggleSelectedPrintoutVariableMark(editor, 'underline')) return;
                        editor.chain().focus().toggleUnderline().run();
                    })}
                >
                    <FormatUnderlinedIcon fontSize="small"/>
                </ToolbarIconButton>

                <Divider flexItem orientation="vertical"/>

                <ToolbarColorMenus
                    disabled={!commandEditor || toolbarState.isBlockSelected}
                    editor={commandEditor}
                    highlightColor={toolbarState.highlightColor}
                    textColor={toolbarState.textColor}
                />

                <ToolbarVariableMenu
                    editor={commandEditor}
                    printoutType={printoutType}
                />

                <ToolbarMoreMenu editor={commandEditor} state={toolbarState}/>

                <ToolbarIconButton
                    disabled={!commandEditor || toolbarState.isBlockSelected}
                    label={t('Clear formatting')}
                    onClick={() => runCommand(clearFormatting)}
                >
                    <FormatClearIcon fontSize="small"/>
                </ToolbarIconButton>

                <Box sx={{flex: 1}}/>

                <ToolbarIconButton
                    disabled={!toolbarState.canUndo}
                    label={t('Undo')}
                    onClick={() => runCommand((editor) => editor.chain().focus().undo().run())}
                >
                    <UndoIcon fontSize="small"/>
                </ToolbarIconButton>
                <ToolbarIconButton
                    disabled={!toolbarState.canRedo}
                    label={t('Redo')}
                    onClick={() => runCommand((editor) => editor.chain().focus().redo().run())}
                >
                    <RedoIcon fontSize="small"/>
                </ToolbarIconButton>
            </Stack>
        </Box>
    );
};

export default PrintoutEditorToolbar;
